import { Fragment } from 'react';
import type { ReactNode } from 'react';

import type { MessageAttachment } from '../../api/types';
import { fileSize } from '../../lib/format';

/**
 * Разметка внутри пузыря: `**жирный**`, `*курсив*`, `` `код` ``.
 * Без сторонних библиотек и без dangerouslySetInnerHTML — собираем узлы React,
 * поэтому разметка из ответа модели не может стать исполняемой.
 */
const INLINE_TOKEN = /(\*\*[^*]+\*\*|__[^_]+__|\*[^*\n]+\*|_[^_\n]+_|`[^`\n]+`)/;

function renderInline(line: string, keyBase: string): ReactNode[] {
    const out: ReactNode[] = [];
    const parts = line.split(INLINE_TOKEN);
    for (let i = 0; i < parts.length; i += 1) {
        const part = parts[i];
        if (!part) continue;
        const key = keyBase + 'i' + i;
        const isStrong =
            part.length >= 5 &&
            ((part.startsWith('**') && part.endsWith('**')) || (part.startsWith('__') && part.endsWith('__')));
        if (isStrong) {
            out.push(<strong key={key}>{part.slice(2, -2)}</strong>);
            continue;
        }
        const isEm =
            part.length >= 3 &&
            ((part.startsWith('*') && part.endsWith('*')) || (part.startsWith('_') && part.endsWith('_')));
        if (isEm) {
            out.push(<em key={key}>{part.slice(1, -1)}</em>);
            continue;
        }
        if (part.length >= 3 && part.startsWith('`') && part.endsWith('`')) {
            out.push(<Fragment key={key}>{part.slice(1, -1)}</Fragment>);
            continue;
        }
        out.push(<Fragment key={key}>{part}</Fragment>);
    }
    return out;
}

/**
 * Блочная разметка. Переводы строк держит сам CSS (`.msg { white-space: pre-wrap }`),
 * поэтому списки не оборачиваются в `<ul>`: маркер `-` заменяется на «•», строка остаётся
 * строкой. Заголовки `#` печатаются полужирными — новых классов не требуется.
 */
export function renderMarkdown(source: string): ReactNode[] {
    const lines = source.replace(/\r\n/g, '\n').split('\n');
    const out: ReactNode[] = [];

    lines.forEach((raw, index) => {
        let line = raw;
        const heading = /^\s{0,3}(#{1,6})\s+(.*)$/.exec(line);
        if (heading) line = heading[2];
        const quote = /^\s{0,3}>\s?(.*)$/.exec(line);
        if (quote) line = quote[1];
        const bullet = /^(\s*)[-*+]\s+(.*)$/.exec(line);
        if (bullet) line = bullet[1] + '• ' + bullet[2];
        if (/^\s{0,3}(-{3,}|\*{3,}|_{3,})\s*$/.test(raw)) line = '———';

        const nodes = renderInline(line, 'l' + index);
        out.push(
            heading ? <strong key={'h' + index}>{nodes}</strong> : <Fragment key={'b' + index}>{nodes}</Fragment>,
        );
        if (index < lines.length - 1) out.push(<Fragment key={'n' + index}>{'\n'}</Fragment>);
    });

    return out;
}

export interface MessageBubbleProps {
    /** Подпись над пузырём (`.msg-from`). */
    from: string;
    variant: 'user' | 'bot';
    /** Обычный текст: переносы строк сохраняет CSS. */
    text?: string | null;
    /** Текст с markdown-разметкой — разбирается `renderMarkdown`. */
    markdown?: string | null;
    /** Ход, отменённый через `undo`: остаётся в ленте, но гаснет. */
    superseded?: boolean;
    attachments?: MessageAttachment[] | null;
    /** Строка `.turn-meta` под пузырём. */
    children?: ReactNode;
}

/** Один ход ленты: подпись, пузырь и, если нужно, строка мета-данных под ним. */
export function MessageBubble({
    from,
    variant,
    text,
    markdown,
    superseded,
    attachments,
    children,
}: MessageBubbleProps) {
    const bubbleClass = 'msg ' + variant + (superseded ? ' superseded' : '');

    return (
        <>
            <div className={variant === 'user' ? 'msg-from right' : 'msg-from'}>{from}</div>
            <div className={bubbleClass}>
                {markdown ? renderMarkdown(markdown) : text}
                {attachments && attachments.length > 0 ? (
                    <div className="msg-files">
                        {attachments.map((file, index) => (
                            <span key={file.name + '#' + index} className="msg-file">
                                {'📎 ' + file.name + (file.size ? ' · ' + fileSize(file.size) : '')}
                            </span>
                        ))}
                    </div>
                ) : null}
                {superseded ? <span className="msg-undone">ход отменён</span> : null}
            </div>
            {children}
        </>
    );
}

export default MessageBubble;
